import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import {
  ORDER_STATUS,
  type OrderStatus,
} from '@seen/core/models/orders/constants'
import { Order } from '@seen/core/models/orders/Order'
import { now } from '@seen/core/utils'
import { Button, type ButtonProps, cn } from '@seen/ui'
import { IconCheck } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { IconOrderStatus } from '#src/components/icons/IconOrderStatus.tsx'
import { useConfirmWithAnnotationStore } from '#src/hooks/useConfirmWithAnnotationStore.ts'
import { useConfirmWithDateAndAnnotation } from '#src/hooks/useConfirmWithDateAndAnnotation.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { toast } from '#src/utils/toast.ts'

interface OrderStatusComboboxProps extends Omit<ButtonProps, 'children'> {
  order: Order
}

const getFlexibleStatuses = (order: Order): OrderStatus[] => {
  const currentStatus = order.status

  // Allow bidirectional transitions for: in-progress <-> coordinated <-> effectuated <-> completed
  const flexibleStatuses: OrderStatus[] = [
    'active',
    'coordinated',
    'effectuated',
    'completed',
  ]

  // For draft and unsigned (on-hold), maintain rigidity - only allow forward progression
  if (currentStatus === 'draft') {
    return ['draft', 'on-hold']
  }

  if (currentStatus === 'on-hold') {
    return []
  }

  // For revoked and entered-in-error, no transitions allowed
  if (currentStatus === 'revoked' || currentStatus === 'entered-in-error') {
    return []
  }

  // For standing orders, only allow transition to completed
  if (order.standing) {
    return ['active', 'completed']
  }

  return flexibleStatuses
}

export function OrderStatusCombobox({
  order: orderProp,
  className,
  ...delegated
}: OrderStatusComboboxProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const { data: order } = useRead(Order, orderProp.id)

  const { open: openConfirmWithAnnotation } = useConfirmWithAnnotationStore()
  const { open: openConfirmWithDateAndAnnotation } =
    useConfirmWithDateAndAnnotation()

  const practitioner = useCurrentPractitioner()
  const createCommunication = useCreateMutation<CommentCommunication>({
    disableDefaultErrorToast: true,
  })
  const patchOrder = usePatchMutation<Order>()

  if (!order) {
    return <Loader />
  }

  const availableStatuses = getFlexibleStatuses(order)
  const options = availableStatuses.map((status) => ({
    value: status,
    label: (
      <div className="flex items-center gap-2">
        <IconOrderStatus status={status} className="size-3.5" />
        {ORDER_STATUS[status]}
        {status === order.status && <IconCheck className="h-4 w-4" />}
      </div>
    ),
    keywords: [ORDER_STATUS[status], status],
  }))

  return (
    <Combobox
      options={options}
      onSelect={(newStatus: OrderStatus): void => {
        const handleStatusChange = (date?: string, note?: string): void => {
          patchOrder.mutate(
            {
              instance: order,
              mutateFn: (resource) => {
                resource.updateStatus(newStatus, { date })
              },
            },
            {
              onSuccess: () => {
                const statusMessages = {
                  coordinated: 'Order successfully coordinated.',
                  effectuated: 'Order successfully marked as effectuated.',
                  completed: 'Order successfully completed.',
                  revoked: 'Order successfully revoked.',
                }
                toast.success(
                  statusMessages[newStatus as keyof typeof statusMessages] ||
                    `Order status updated to ${newStatus}.`
                )
                if (note?.trim() && newStatus === 'revoked') {
                  createCommunication.mutate(
                    new CommentCommunication({
                      message: note,
                      sender: practitioner.reference,
                      subject: order.participant,
                      partOf: [order.reference],
                      orderRevokeReason: true,
                      sent: now(),
                    }),
                    {
                      onError: () =>
                        toast.error(
                          'Order revoked, but failed to save the revoke reason.'
                        ),
                    }
                  )
                }
              },
            }
          )
        }

        if (newStatus === 'coordinated' || newStatus === 'effectuated') {
          openConfirmWithDateAndAnnotation({
            title: `Are you sure you want to mark this order as ${newStatus}?`,
            description: `This action cannot be undone. This will mark the order as ${newStatus}.`,
            defaultDate: now(),
            onConfirm: (_, date) => handleStatusChange(date),
          })
        } else if (
          newStatus === 'completed' ||
          newStatus === 'revoked' ||
          newStatus === 'active'
        ) {
          let label = ''
          if (newStatus === 'completed') {
            label = 'complete'
          } else if (newStatus === 'revoked') {
            label = 'revoke'
          } else if (newStatus === 'active') {
            label = 'in progress'
          }
          openConfirmWithAnnotation({
            title: `Are you sure you want to mark this Order as ${label}?`,
            description: `This action cannot be undone. This will mark the order as ${label}.`,
            onConfirm: (note: string) => handleStatusChange(undefined, note),
          })
        } else {
          handleStatusChange()
        }
      }}
    >
      <Button
        variant="unstyled"
        className={cn(
          'p-0! h-full hover:brightness-hover-icon data-[state=open]:brightness-hover-icon',
          className
        )}
        loading={patchOrder.isPending}
        disabled={!options.length || isReadOnly}
        {...delegated}
      >
        <IconOrderStatus
          status={order.status}
          isSubmitting={order.isSubmitting}
        />
      </Button>
    </Combobox>
  )
}
